import cn from "classnames";
import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { Flyout } from "../flyout";
import {
  getFlyoutPositionsOrder,
  mapTooltipPlacementToFlyoutPosition,
  splitPosition,
} from "./utils";
import type { TooltipProps } from "./types";
import type { Position } from "../flyout/types";
import styles from "./styles.module.scss";

export const Tooltip = ({
  text,
  children,
  className,
  placement: initialPlacement = "bottom-right",
  light = false,
  shown = false,
  dataTestId = "tooltip",
}: TooltipProps) => {
  const anchorRef = useRef(null);
  const [position, setPosition] = useState<Position>(
    mapTooltipPlacementToFlyoutPosition(initialPlacement)
  );

  const [side, align] = splitPosition(position);

  useEffect(() => {
    setPosition(mapTooltipPlacementToFlyoutPosition(initialPlacement));
  }, [initialPlacement]);

  const handleChangePosition = useCallback((updatedPosition: Position) => {
    setPosition(updatedPosition);
  }, []);

  const flyoutPositions = useMemo(
    () =>
      getFlyoutPositionsOrder(
        mapTooltipPlacementToFlyoutPosition(initialPlacement)
      ),
    [initialPlacement]
  );

  useEffect(() => {
    if (!shown) {
      setPosition(mapTooltipPlacementToFlyoutPosition(initialPlacement));
    }
  }, [initialPlacement, shown]);

  return (
    <div
      className={styles.anchor}
      ref={anchorRef}
      data-test-id={`${dataTestId}-anchor`}
    >
      {children}
      {shown && (
        <Flyout
          className={cn(className, styles.flyout, styles[side])}
          parentRef={anchorRef}
          positionsOrder={flyoutPositions}
          onChangePosition={handleChangePosition}
          dataTestId={dataTestId}
        >
          <>
            <div
              className={cn(styles["text-box"], styles[side], {
                [styles.light]: light,
              })}
              data-test-id={`${dataTestId}-text`}
            >
              <p className={cn(styles.text, { [styles.light]: light })}>
                {text}
              </p>
            </div>
            <div
              className={cn(styles["tail-box"], styles[side], styles[align])}
              data-test-id={`${dataTestId}-tail`}
            >
              <i
                className={cn(styles.tail, styles[side], styles[align], {
                  [styles.light]: light,
                })}
                data-test-id={`${dataTestId}-tail`}
              />
            </div>
          </>
        </Flyout>
      )}
    </div>
  );
};
